import Router from 'next/router';
import Layout from '@/components/layout/Layout';
import { cn } from '@/utils/styles';
import StopSignIcon from '@/components/ui/icons/ScoutDatasets/StopSignIcon';
import TrafficLightIcon from '@/components/ui/icons/ScoutDatasets/TrafficLightIcon';
import SpeedLimitSignIcon from '@/components/ui/icons/ScoutDatasets/SpeedLimitSignIcon';
import NoUTurnNoLeftTurnIcon from '@/components/ui/icons/ScoutDatasets/NoUTurnNoLeftTurnIcon';
import BusStopIcon from '@/components/ui/icons/ScoutDatasets/BusStopIcon';
import SpeedCameraIcon from '@/components/ui/icons/ScoutDatasets/SpeedCameraIcon';
import BikeSignIcon from '@/components/ui/icons/ScoutDatasets/BikeSignIcon';
import FireHydrantIcon from '@/components/ui/icons/ScoutDatasets/FireHydrantIcon';
import ParkingSignIcon from '@/components/ui/icons/ScoutDatasets/ParkingSignIcon';
import HighwayExitSignIcon from '@/components/ui/icons/ScoutDatasets/HighwayExitSignIcon';

// TODO: generate these dynamically after the structure is set up in Contentful.
export const DATASETS = [
  {
    feature: 'Speed Cameras',
    slug: 'speed-cameras',
    overviewArticle: {
      title: 'The Ultimate Guide to Speed Cameras',
      slug: 'the-ultimate-guide-to-speed-cameras',
    },
    commonQuestionArticles: [
      {
        title: 'Speed Camera Myths',
        slug: 'speed-camera-myths',
      },
      {
        title: 'How Do Speed Cameras Influence Autonomous Driving Algorithms?',
        slug: 'how-do-speed-cameras-influence-autonomous-driving-algorithms',
      },
      {
        title: 'What States Have Speed Cameras on Highways?',
        slug: 'what-states-have-speed-cameras-on-highways',
      },
      {
        title: 'Have Studies Been Done on Speed Cameras?',
        slug: 'have-studies-been-done-on-speed-cameras',
      },
      {
        title: 'Can Autonomous Cars Detect Speed Cameras?',
        slug: 'can-autonomous-cars-detect-speed-cameras',
      },
      {
        title: 'How Do Municipalities and Governments Use Speed Cameras?',
        slug: 'how-do-municipalities-and-governments-use-speed-cameras',
      },
      {
        title: 'What Do Speed Cameras Look Like in France?',
        slug: 'what-do-speed-cameras-look-like-in-france',
      },
      {
        title: 'How Do You Find Speed Cameras in Major Cities?',
        slug: 'how-do-you-find-speed-cameras-in-major-cities',
      },
    ],
  },
  {
    feature: 'Highway Exit Signs',
    slug: 'highway-exit-signs',
    overviewArticle: {
      title: 'The Ultimate Guide to Highway Exit Signs',
      slug: 'guide-to-highway-exit-signs',
    },
    commonQuestionArticles: [
      {
        title: 'How Do Exit Signs Reduce Highway Accidents and Congestion?',
        slug: 'how-do-exit-signs-reduce-highway-accidents-and-congestion',
      },
      {
        title: 'Do Highway Exit Signs Have Standard Colors and Materials?',
        slug: 'highway-exit-sign-standard-color-material',
      },
      {
        title: 'What Are the Rules for Spacing Exit Signs on Freeways?',
        slug: 'rules-for-spacing-exit-signs-on-freeways',
      },
      {
        title: 'Can Self-Driving Cars Recognize Damaged Freeway Signs?',
        slug: 'self-driving-cars-detect-damaged-signs',
      },
      {
        title: 'What Are The Widest Highways In The World',
        slug: 'what-are-the-widest-highways-in-the-world',
      },
    ],
  },
  {
    feature: 'Traffic Lights',
    slug: 'traffic-lights',
    overviewArticle: {
      title: 'Traffic Lights: Ultimate Guide Overview',
      slug: 'traffic-lights-ultimate-guide',
    },
    commonQuestionArticles: [
      {
        title: 'Stop Signs vs. Traffic Lights: Which Is More Efficient?',
        slug: 'stop-signs-vs-traffic-lights',
      },
      {
        title: 'What Intersection in the World Has the Most Traffic Lights?',
        slug: 'intersection-with-most-traffic-lights',
      },
    ],
  },
  {
    feature: 'Stop Signs',
    slug: 'stop-signs',
    overviewArticle: {
      title: 'Stop Signs: Ultimate Guide',
      slug: 'stop-signs-ultimate-guide',
    },
    commonQuestionArticles: [
      {
        title: 'What Should I Do If Waymo Runs a Stop Sign?',
        slug: 'what-should-i-do-if-waymo-runs-a-stop-sign',
      },
      {
        title: 'Do Stop Signs Differ by Country?',
        slug: 'do-stop-signs-differ-by-country',
      },
      {
        title: 'What Country Has the Most “Four-Way Stop” Intersections?',
        slug: 'most-four-way-stop-intersections',
      },
      {
        title: 'Stop Signs vs. Traffic Lights: Which Is More Efficient?',
        slug: 'stop-signs-vs-traffic-lights',
      },
      {
        title:
          'How Can Digital Mapping of Stop Signs Improve Fleet Management?',
        slug: 'digital-mapping-stop-signs-improves-fleet-management',
      },
      {
        title: 'Who Chose the Shape and Color of the Stop Sign?',
        slug: 'who-chose-shape-and-color-stop-sign',
      },
      {
        title: 'How Is Stop Sign Data Used in Geospatial Mapping and GIS?',
        slug: 'stop-sign-data-in-geospatial-mapping-and-gis',
      },
      {
        title: 'How Have Stop Signs Evolved for Modern Traffic Management?',
        slug: 'stop-signs-modern-traffic-management',
      },
    ],
  },
  {
    feature: 'Parking Signs',
    slug: 'parking-signs',
    overviewArticle: {
      title: 'The Ultimate Guide to Parking Signs',
      slug: 'guide-to-parking-signs',
    },
    commonQuestionArticles: [
      {
        title:
          'How Can Municipalities Use Parking Sign Data to Manage Traffic Congestion?',
        slug: 'municipalities-parking-sign-data-to-manage-traffic-congestion',
      },
      {
        title:
          'How Do Real Estate Agents Use Parking Sign Information to Increase Property Value?',
        slug: 'parking-sign-information-increase-property-value',
      },
      {
        title: 'How Do Autonomous Vehicles Use Parking Sign Data?',
        slug: 'autonomous-vehicles-parking-sign-data',
      },
      {
        title: 'What Do Different Types of Parking Signs Mean?',
        slug: 'different-parking-signs',
      },
      {
        title: 'How Do Time-Limited Parking Signs Work?',
        slug: 'how-time-limited-parking-signs-work',
      },
      {
        title: 'Why Are Parking Signs So Confusing?',
        slug: 'parking-signs-are-confusing',
      },
    ],
  },
  {
    feature: 'Speed Signs',
    slug: 'speed-signs',
    overviewArticle: {
      title: 'The Ultimate Guide to Speed Signs',
      slug: 'guide-to-speed-signs',
    },
    commonQuestionArticles: [
      {
        title: 'What Happens When Speed Signs Are Incorrectly Placed?',
        slug: 'speed-signs-incorrectly-placed',
      },
      {
        title: 'Where Is the Fastest Speed Limit on Earth?',
        slug: 'fastest-speed-limit-on-earth',
      },
      {
        title: 'How Do Cities Use Speed Sign Data to Plan Safer Communities?',
        slug: 'speed-sign-data-plan-safer-communities',
      },
      {
        title: 'How Do Mapping Companies Monitor and Update Speed Sign Data?',
        slug: 'monitor-and-update-speed-sign-data',
      },
      {
        title: 'How Do Digital Speed Signs Work?',
        slug: 'how-digital-speed-signs-work',
      },
    ],
  },
  {
    feature: 'Turn Restrictions',
    slug: 'turn-restrictions',
    overviewArticle: {
      title: 'The Ultimate Guide to Turn Restrictions',
      slug: 'the-ultimate-guide-to-turn-restrictions',
    },
    commonQuestionArticles: [
      {
        title:
          'Understanding the Difference Between Permanent and Temporary Turn Restriction Signs',
        slug: 'permanent-and-temporary-turn-restriction-signs',
      },
      {
        title:
          'How Do Cities Use Turn Restriction Signs to Improve Traffic Flow?',
        slug: 'cities-turn-restriction-improve-traffic',
      },
      {
        title: 'What City Has the Most Turn Restrictions, and Why?',
        slug: 'city-most-turn-restrictions',
      },
      {
        title: 'Autonomous Vehicles and Turn Restrictions',
        slug: 'autonomous-vehicles-navigate-turn-restrictions',
      },
    ],
  },
  {
    feature: 'Bicycle Signs',
    slug: 'bicycle-signs',
    overviewArticle: {
      title: 'The Ultimate Guide to Bicycle Signs',
      slug: 'guide-to-bicycle-signs',
    },
    commonQuestionArticles: [],
  },
  {
    feature: 'Bus Stops',
    slug: 'bus-stops',
    overviewArticle: {
      title: 'The Ultimate Guide to Bus Stops',
      slug: 'guide-to-bus-stops',
    },
    commonQuestionArticles: [
      {
        title: 'What Is the Busiest Bus Stop in the World by Daily Passengers?',
        slug: 'busiest-bus-stop-in-the-world',
      },
      {
        title: 'How Are Bus Stop Locations Determined?',
        slug: 'how-bus-stop-locations-determined',
      },
    ],
  },
  {
    feature: 'Fire Hydrants',
    slug: 'fire-hydrants',
    overviewArticle: {
      title: 'The Ultimate Guide to Fire Hydrants',
      slug: 'guide-to-fire-hydrants',
    },
    commonQuestionArticles: [
      {
        title: 'How Do Cities Create Graffiti-Proof Fire Hydrants?',
        slug: 'graffiti-proof-fire-hydrants',
      },
      {
        title: 'What Is the Oldest Fire Hydrant Still in Use Today?',
        slug: 'oldest-fire-hydrant',
      },
      {
        title: 'Where Is the World’s Most Famous Golden Fire Hydrant?',
        slug: 'famous-golden-fire-hydrant',
      },
      {
        title: 'What Is the Fee for Parking in Front of a Fire Hydrant?',
        slug: 'parking-fee-fire-hydrant',
      },
      {
        title:
          'How Do Mapping Companies Track Fire Hydrant Locations for City Maps?',
        slug: 'mapping-fire-hydrant-locations-city-maps',
      },
      {
        title: 'How Are Fire Hydrants Inspected and Tested for Functionality?',
        slug: 'fire-hydrants-functionality',
      },
      {
        title: 'What Role Do Fire Hydrants Play in Urban Planning?',
        slug: 'fire-hydrants-in-urban-planning',
      },
      {
        title: 'How Do Different Cities Color-Code Their Fire Hydrants?',
        slug: 'how-cities-color-fire-hydrants',
      },
      {
        title:
          'How Do Municipalities Use Fire Hydrant Data for Emergency Planning?',
        slug: 'fire-hydrant-data-for-emergency-planning',
      },
    ],
  },
];

const tableTitles = ['Icon', 'Feature', 'Category'] as const;
const TABLE_DATA = [
  {
    Icon: <SpeedCameraIcon width={30} />,
    Feature: 'Speed Cameras',
    Category: 'Transportation',
    slug: 'speed-cameras',
  },
  {
    Icon: <HighwayExitSignIcon width={30} />,
    Feature: 'Highway Exit Signs',
    Category: 'Transportation',
    slug: 'highway-exit-signs',
  },
  {
    Icon: <TrafficLightIcon width={30} />,
    Feature: 'Traffic Lights',
    Category: 'Transportation',
    slug: 'traffic-lights',
  },
  {
    Icon: <StopSignIcon width={30} />,
    Feature: 'Stop Signs',
    Category: 'Transportation',
    slug: 'stop-signs',
  },
  {
    Icon: <ParkingSignIcon width={30} />,
    Feature: 'Parking Signs',
    Category: 'Infrastructure',
    slug: 'parking-signs',
  },
  {
    Icon: <SpeedLimitSignIcon width={30} />,
    Feature: 'Speed Signs',
    Category: 'Transportation',
    slug: 'speed-signs',
  },
  {
    Icon: <NoUTurnNoLeftTurnIcon width={30} />,
    Feature: 'Turn Restrictions',
    Category: 'Transportation',
    slug: 'turn-restrictions',
  },
  {
    Icon: <BikeSignIcon width={30} />,
    Feature: 'Bicycle Signs',
    Category: 'Mobility',
    slug: 'bicycle-signs',
  },
  {
    Icon: <BusStopIcon width={30} />,
    Feature: 'Bus Stops',
    Category: 'Infrastructure',
    slug: 'bus-stops',
  },
  {
    Icon: <FireHydrantIcon width={30} />,
    Feature: 'Fire Hydrants',
    Category: 'Infrastructure',
    slug: 'fire-hydrants',
  },
] as const;

export default function Page() {
  return (
    <Layout>
      <div className="overflow-x-auto">
        <table className="w-full table-auto border border-neutral-700">
          <thead>
            <tr className="border-b border-neutral-700">
              {tableTitles.map((title, i) => (
                <th
                  key={title}
                  className={cn(
                    'px-4  py-2 text-left text-sm text-neutral-600',
                    { 'w-3 pr-0': i === 0 },
                  )}>
                  {i !== 0 ? title : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {TABLE_DATA.map(data => (
              <tr
                key={data.Feature}
                className="cursor-pointer border-b"
                onClick={() => {
                  Router.push(`/scout/datasets/${data.slug}`);
                }}>
                {tableTitles.map(title => (
                  <td
                    key={`${data.Feature}-${title}`}
                    className={cn('px-4 py-2 text-sm', {
                      'w-3 pr-0': title === 'Icon',
                    })}>
                    {data[title]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
